// CB:BEGIN useAuthFetch
/**
 * Returns an authenticated fetch function that attaches Clerk Bearer token (or dev token)
 * so backend requireClerkSession sees the user. Use for any /api/* call that requires auth.
 *
 * Also forwards X-Impersonate-Token when an admin support session is active so the backend
 * override middleware can act as the impersonated user on every request path.
 */
import { useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';

const IMPERSONATION_SESSION_KEY = 'cb_impersonation';
const IMPERSONATION_TTL_MS = 30 * 60 * 1000;

function getImpersonationToken(): string | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.token && parsed?.startedAt && Date.now() - parsed.startedAt < IMPERSONATION_TTL_MS) {
      return parsed.token as string;
    }
  } catch {}
  return null;
}

export function useAuthFetch() {
  const { getToken } = useAuth();

  return useCallback(
    async (url: string, init?: RequestInit): Promise<Response> => {
      let token: string | null = null;
      try {
        const devAuth = localStorage.getItem('devAuth');
        if (devAuth) {
          const parsed = JSON.parse(devAuth);
          if (parsed?.isDevelopment && parsed?.token) token = parsed.token;
        }
      } catch {}
      if (!token) token = await getToken();
      const headers = new Headers(init?.headers ?? {});
      if (token) headers.set('Authorization', `Bearer ${token}`);
      // CB:BEGIN impersonation-header-authfetch
      const impToken = getImpersonationToken();
      if (impToken) headers.set('X-Impersonate-Token', impToken);
      // CB:END impersonation-header-authfetch
      if (import.meta.env.DEV) {
        console.log('[authFetch]', url.split('?')[0], { hasAuth: !!token, impersonating: !!impToken });
      }
      return fetch(url, { ...init, credentials: 'include', headers });
    },
    [getToken]
  );
}
// CB:END useAuthFetch
